import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Archive, Columns3, List as ListIcon, Plus, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useMe } from '~/api/auth';
import { useServerClock } from '~/api/serverClock';
import { projectsQueryOptions } from '~/api/projects';
import {
  type BoardStage,
  listsQueryOptions,
  occurrencesQueryOptions,
  tasksPageQueryOptions,
  type TasksFilter,
} from '~/api/work';
import { ArchiveView } from '~/features/work/ArchiveView';
import { Board } from '~/features/work/Board';
import { OccurrenceSheet } from '~/features/work/OccurrenceSheet';
import { RepeatingToday } from '~/features/work/RepeatingToday';
import { splitRepeating } from '~/features/work/recurrence';
import { TaskList, type TaskSection } from '~/features/work/TaskList';

/** Tasks per section before "Show more". */
const SECTION_PAGE = 24;
import { TaskSheet } from '~/features/work/TaskSheet';
import { addDays, calendarDate } from '~/lib/format';
import { Button } from '~/ui/Button';
import { Chips } from '~/ui/Chips';
import { ErrorNotice } from '~/ui/ErrorNotice';
import { PageHeader } from '~/ui/PageHeader';
import { Skeleton } from '~/ui/Skeleton';

const VIEWS = ['board', 'list', 'archive'] as const;
const FILTERS = ['all', 'mine', 'high', 'delayed', 'pool'] as const;
type View = (typeof VIEWS)[number];
type Filter = (typeof FILTERS)[number];

interface Search {
  view: View;
  filter: Filter;
  q?: string;
  task?: string;
  run?: string;
}

export const Route = createFileRoute('/_app/tasks')({
  validateSearch: (s: Record<string, unknown>): Search => ({
    view: (VIEWS as readonly string[]).includes(String(s['view'])) ? (s['view'] as View) : 'board',
    filter: (FILTERS as readonly string[]).includes(String(s['filter']))
      ? (s['filter'] as Filter)
      : // Your own work first; All is a click away.
        'mine',
    q: typeof s['q'] === 'string' && s['q'] ? s['q'] : undefined,
    task: typeof s['task'] === 'string' && s['task'] ? s['task'] : undefined,
    run: typeof s['run'] === 'string' && s['run'] ? s['run'] : undefined,
  }),
  component: TasksPage,
});

function TasksPage() {
  const me = useMe();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/tasks' });
  const now = useServerClock(60_000);
  const tz = me.person.tz ?? me.workspace.tz;
  const today = calendarDate(now, tz);
  const lists = useQuery(listsQueryOptions);
  // The column a new task starts in, when it was added from the board's + button.
  const [newStage, setNewStage] = useState<BoardStage | undefined>(undefined);

  const go = (patch: Partial<Search>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  const filterOptions = [
    { value: 'all' as const, label: 'All' },
    { value: 'mine' as const, label: 'My tasks' },
    { value: 'high' as const, label: 'High priority' },
    { value: 'delayed' as const, label: search.view === 'board' ? 'Slipped' : 'In the pool' },
  ].map((o) =>
    o.value === 'delayed' && search.view === 'list'
      ? { value: 'pool' as const, label: 'In the pool' }
      : o,
  );

  return (
    <div className="mx-auto max-w-[1400px]">
      <PageHeader
        eyebrow="Work"
        title="Tasks"
        subtitle={
          search.view === 'board'
            ? 'Drag a card to another column, or open it to act on it.'
            : search.view === 'archive'
              ? 'Finished and put-away work. Search it, or bring something back.'
              : 'Every duty written once, by section.'
        }
        actions={
          <>
            <div
              role="group"
              aria-label="View"
              data-tour="task-view"
              className="inline-flex rounded-control border border-line p-0.5"
            >
              <button
                type="button"
                aria-pressed={search.view === 'board'}
                onClick={() =>
                  go({ view: 'board', filter: search.filter === 'pool' ? 'mine' : search.filter })
                }
                className={`inline-flex h-9 items-center gap-1.5 rounded-[6px] px-3 text-sm ${search.view === 'board' ? 'bg-accent-soft text-accent-ink' : 'text-ink-2 hover:bg-paper-2'}`}
              >
                <Columns3 size={16} aria-hidden /> Board
              </button>
              <button
                type="button"
                aria-pressed={search.view === 'list'}
                onClick={() =>
                  go({ view: 'list', filter: search.filter === 'delayed' ? 'mine' : search.filter })
                }
                className={`inline-flex h-9 items-center gap-1.5 rounded-[6px] px-3 text-sm ${search.view === 'list' ? 'bg-accent-soft text-accent-ink' : 'text-ink-2 hover:bg-paper-2'}`}
              >
                <ListIcon size={16} aria-hidden /> List
              </button>
              <button
                type="button"
                aria-pressed={search.view === 'archive'}
                onClick={() => go({ view: 'archive', filter: 'mine' })}
                className={`inline-flex h-9 items-center gap-1.5 rounded-[6px] px-3 text-sm ${search.view === 'archive' ? 'bg-accent-soft text-accent-ink' : 'text-ink-2 hover:bg-paper-2'}`}
              >
                <Archive size={16} aria-hidden /> Archive
              </button>
            </div>
            <Button
              icon={<Plus size={16} aria-hidden />}
              data-tour="task-new"
              onClick={() => {
                setNewStage(undefined);
                go({ task: 'new' });
              }}
            >
              New task
            </Button>
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3" data-tour="task-filter">
        {search.view !== 'archive' && (
          <Chips
            label="Filter"
            options={filterOptions}
            value={search.filter}
            onChange={(f) => go({ filter: f })}
          />
        )}
        <label className="relative ml-auto block w-full sm:w-64">
          <Search
            size={16}
            aria-hidden
            className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-3"
          />
          <input
            type="search"
            aria-label="Search tasks"
            placeholder="Search"
            value={search.q ?? ''}
            onChange={(e) => go({ q: e.target.value || undefined })}
            className="h-9 w-full rounded-full border border-line bg-paper pl-9 pr-3 text-sm placeholder:text-ink-3 focus:border-accent"
          />
        </label>
      </div>

      {search.view === 'archive' ? (
        <ArchiveView q={search.q} onOpen={(id) => go({ task: id })} />
      ) : search.view === 'board' ? (
        <BoardView
          today={today}
          tz={tz}
          filter={search.filter}
          q={search.q}
          onOpen={(id) => go({ run: id })}
          onNew={(stage) => {
            setNewStage(stage);
            go({ task: 'new' });
          }}
        />
      ) : (
        <ListView
          today={today}
          filter={search.filter}
          q={search.q}
          onOpen={(id) => go({ task: id })}
          onOpenRun={(id) => go({ run: id })}
        />
      )}

      <TaskSheet
        id={search.task ?? null}
        newStage={newStage}
        lists={lists.data ?? []}
        today={today}
        onClose={() => go({ task: undefined })}
        onOpenRun={(id) => go({ task: undefined, run: id })}
        onOpenTask={(id) => go({ task: id })}
      />
      <OccurrenceSheet
        id={search.run ?? null}
        today={today}
        onClose={() => go({ run: undefined })}
        onOpenTask={(id) => go({ run: undefined, task: id })}
      />
    </div>
  );
}

function BoardView({
  today,
  tz,
  filter,
  q,
  onOpen,
  onNew,
}: {
  today: string;
  tz: string;
  filter: Filter;
  q?: string;
  onOpen: (id: string) => void;
  onNew: (stage: BoardStage) => void;
}) {
  const me = useMe();
  const range = { from: addDays(today, -7), to: addDays(today, 6) };
  // Include undated runs so one-time tasks with no due date, project tasks among them,
  // show on the board instead of only in the list; and unfinished work of any date, so a
  // task due next month or missed weeks ago is here as it is on its project's board.
  const occurrences = useQuery(
    occurrencesQueryOptions({ ...range, includeUndated: true, includeOpen: true }),
  );
  const projects = useQuery(projectsQueryOptions());
  const projectById = useMemo(
    () =>
      new Map(
        (projects.data ?? []).map((p) => [p.id, { key: p.key, name: p.name, color: p.color }]),
      ),
    [projects.data],
  );
  const { rows, repeating } = useMemo(() => {
    const data = occurrences.data ?? [];
    // Slipped lists every missed day; otherwise today's repeating duties are listed
    // together at the top of to do, and stay as cards only once they are under way.
    const split =
      filter === 'delayed' ? { cards: data, repeating: [] } : splitRepeating(data, today);
    const needle = q?.trim().toLowerCase();
    const keep = (o: (typeof data)[number]) => {
      if (filter === 'mine' && o.personId !== me.person.id) return false;
      if (filter === 'high' && o.priority !== 'high') return false;
      if (filter === 'delayed' && !(o.overBudget || o.overdue || o.status === 'missed'))
        return false;
      if (needle && !o.taskTitle.toLowerCase().includes(needle)) return false;
      // Finished runs from earlier days stay in history, not on the board. The day it
      // was finished counts, so work over several days that ended today shows in Done.
      if (o.status === 'done' || o.status === 'skipped') {
        const finished = o.completedAt
          ? calendarDate(new Date(o.completedAt), tz)
          : o.scheduledDate;
        if (finished !== null && finished < today) return false;
      }
      return true;
    };
    return { rows: split.cards.filter(keep), repeating: split.repeating.filter(keep) };
  }, [occurrences.data, filter, q, me.person.id, today, tz]);

  if (occurrences.isPending) return <Skeleton lines={8} />;
  if (occurrences.isError)
    return <ErrorNotice error={occurrences.error} onRetry={() => occurrences.refetch()} />;
  return (
    <Board
      occurrences={rows}
      today={today}
      serverTime={new Date(occurrences.dataUpdatedAt).toISOString()}
      onOpen={onOpen}
      projectById={projectById}
      onNew={onNew}
      holdUpcoming
      repeating={repeating}
      showRepeatingPerson={filter !== 'mine'}
    />
  );
}

function ListView({
  today,
  filter,
  q,
  onOpen,
  onOpenRun,
}: {
  today: string;
  filter: Filter;
  q?: string;
  onOpen: (id: string) => void;
  onOpenRun: (id: string) => void;
}) {
  const me = useMe();
  const lists = useQuery(listsQueryOptions);
  // Each section loads a page; "Show more" asks that section for more.
  const [limits, setLimits] = useState<Record<string, number>>({});
  const base: TasksFilter = {
    ...(q ? { q } : {}),
    ...(filter === 'mine' ? { ownerId: me.person.id } : {}),
    ...(filter === 'high' ? { priority: 'high' as const } : {}),
    ...(filter === 'pool' ? { pool: true } : {}),
  };
  const pages = useQueries({
    queries: (lists.data ?? []).map((l) => ({
      ...tasksPageQueryOptions({ ...base, listId: l.id, limit: limits[l.id] ?? SECTION_PAGE }),
      placeholderData: keepPreviousData,
    })),
  });
  const projects = useQuery(projectsQueryOptions());
  // The viewer's open runs, so each row can start/pause/complete without opening the task.
  const runs = useQuery(
    occurrencesQueryOptions({
      personId: me.person.id,
      from: addDays(today, -31),
      to: addDays(today, 31),
      includeUndated: true,
      includeOpen: true,
    }),
  );
  const sections: TaskSection[] = (lists.data ?? []).map((l, i) => {
    const page = pages[i];
    const shown = limits[l.id] ?? SECTION_PAGE;
    return {
      list: l,
      tasks: page?.data?.tasks ?? [],
      total: page?.data?.total ?? 0,
      loadingMore: page?.isFetching && page.isPlaceholderData,
      // The API gives at most 200 at a time; past that, search narrows it.
      onMore:
        shown < 200
          ? () => setLimits((m) => ({ ...m, [l.id]: Math.min(200, shown + SECTION_PAGE) }))
          : undefined,
    };
  });
  const failed = pages.find((p) => p.isError);

  // Today's repeating duties of the viewer, listed together above the sections.
  const repeating = useMemo(() => {
    const needle = q?.trim().toLowerCase();
    return splitRepeating(runs.data ?? [], today).repeating.filter(
      (o) =>
        o.personId === me.person.id &&
        (filter !== 'high' || o.priority === 'high') &&
        (!needle || o.taskTitle.toLowerCase().includes(needle)),
    );
  }, [runs.data, today, me.person.id, filter, q]);

  // One actionable run per task: the one running, else today's, else the earliest still open.
  // A repeating duty offers today's copy, never a missed one from days back.
  const runByTask = useMemo(() => {
    const { cards, repeating: todays } = splitRepeating(runs.data ?? [], today);
    const open = [...new Map([...cards, ...todays].map((o) => [o.id, o])).values()].filter(
      (o) =>
        o.personId === me.person.id &&
        (o.status === 'active' ||
          o.status === 'pending' ||
          o.status === 'paused' ||
          o.status === 'missed'),
    );
    const rank = (o: (typeof open)[number]) =>
      o.status === 'active' ? 0 : o.scheduledDate === today ? 1 : o.scheduledDate === null ? 2 : 3;
    const map = new Map<string, (typeof open)[number]>();
    for (const o of open) {
      const cur = map.get(o.taskId);
      if (
        !cur ||
        rank(o) < rank(cur) ||
        (rank(o) === rank(cur) && (o.scheduledDate ?? today) < (cur.scheduledDate ?? today))
      )
        map.set(o.taskId, o);
    }
    return map;
  }, [runs.data, me.person.id, today]);

  if (lists.isPending || pages.some((p) => p.isPending)) return <Skeleton lines={8} />;
  if (failed) return <ErrorNotice error={failed.error} onRetry={() => failed.refetch()} />;
  if (lists.isError) return <ErrorNotice error={lists.error} onRetry={() => lists.refetch()} />;
  return (
    <>
      <RepeatingToday
        runs={repeating}
        onOpen={onOpenRun}
        layout="grid"
        projectOf={(o) => {
          const p = o.projectId ? projects.data?.find((x) => x.id === o.projectId) : undefined;
          return p ? { key: p.key, name: p.name, color: p.color } : undefined;
        }}
      />
      <TaskList
        sections={sections}
        today={today}
        onOpen={onOpen}
        runByTask={runByTask}
        serverTime={new Date(runs.dataUpdatedAt).toISOString()}
      />
    </>
  );
}
